import { useEffect, useState } from "react";
import { useDebounce } from "../../../../hooks/useDebounce";
import { formatTimestamp } from "../../../../utils";
import Button from "../../../../common/components/xepp/button/Button";
import { FilterIcon2 } from "../../../../assets/icons";
import EmptyState from "../../../../common/components/xepp/empty-state/EmptyState";
import SearchInput from "../../../../common/components/xepp/search-input/SearchInput";
import { TablePagination } from "../../../../common/components/xepp/table-pagination/TablePagination";
import DataTable, { ColumnType } from "../../../../common/components/xepp/table/DataTable";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import AssignStepModal from "./AssignStepModal";
import SearchableMultiselectDropdown from "../../../../common/components/xepp/searchable-multiselect-dropdown/SearchableMultiselectDropdown";

import styles from "./steps.module.scss";
import Can from "../../../../guards/Can";
import { UtilityPermissions } from "../../../../model/permissions/permissions";
import { StepInstanceResponse } from "../../../../model/workflows/step-instance";
import { StepStatus } from "../../../../model/workflows/supervisor-steps";
import { useLazyGetSupervisorStepsQuery, useSupervisorUnassignStepMutation } from "../../../../service/customersupport-service";
import { useLazyGetUsersForFilterQuery } from "../../../../service/user-service";
import { WorkflowsMenuActiveIcon } from "../../../../assets/sidemenu-icons";

interface SupervisorStepsTableProps {
	departmentId: string;
	stepStatus: StepStatus;
	fromDate: Date | null;
	toDate: Date | null;
	onOpenDateFilter: () => void;
	onClearDateFilter: () => void;
	dateFilterLabel: string;
	hasActiveDateFilter: boolean;
}

export const SupervisorStepsTable = ({
	departmentId,
	stepStatus,
	fromDate,
	toDate,
	onOpenDateFilter,
	onClearDateFilter,
	dateFilterLabel,
	hasActiveDateFilter,
}: SupervisorStepsTableProps) => {
	const [searchTerm, setSearchTerm] = useState<string>("");
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	const [nameSearchTerm, setNameSearchTerm] = useState<string>("");
	const debouncedNameSearchTerm = useDebounce(nameSearchTerm, 500);
	const [page, setPage] = useState(1);
	const pageSize = 15;
	const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

	const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
	const [selectedStepInstance, setSelectedStepInstance] = useState<StepInstanceResponse | null>(null);
	const [assignModalMode, setAssignModalMode] = useState<'assign' | 'reassign'>('assign');
	const [unassigningStepId, setUnassigningStepId] = useState<string | null>(null);

	const [getSupervisorSteps, { data, isFetching }] = useLazyGetSupervisorStepsQuery();
	const [unassignStep] = useSupervisorUnassignStepMutation();
	const [getUsersForFilter, { data: usersFilterData, isFetching: isLoadingUsers }] = useLazyGetUsersForFilterQuery();

	const { count } = data ?? {};
	const tableData = isFetching ? [] : (data?.items ?? []);

	// Fetch filter options on mount and when name search changes
	useEffect(() => {
		const filterParams: any = { departmentId };
		if (debouncedNameSearchTerm) {
			filterParams.search = debouncedNameSearchTerm;
		}
		getUsersForFilter(filterParams);
	}, [departmentId, debouncedNameSearchTerm, getUsersForFilter]);

	useEffect(() => {
		const queryParams: any = {
			page,
			pageSize,
			departmentId,
			stepStatus,
		};

		if (debouncedSearchTerm) queryParams.search = debouncedSearchTerm;
		if (fromDate) queryParams.from = fromDate.toISOString();
		if (toDate) queryParams.to = toDate.toISOString();
		if (selectedUserIds.length > 0) queryParams.userIds = selectedUserIds;

		getSupervisorSteps(queryParams);
	}, [page, pageSize, departmentId, stepStatus, debouncedSearchTerm, fromDate, toDate, selectedUserIds, getSupervisorSteps]);

	useEffect(() => {
		if (searchTerm !== debouncedSearchTerm && searchTerm !== "") {
			setPage(1);
		}
	}, [searchTerm, debouncedSearchTerm]);

	// Combine customers and staff for name filter
	const allUsers = [
		...(usersFilterData?.customers ?? []).map(c => ({ id: c.id, label: `Customer: ${c.displayName}` })),
		...(usersFilterData?.staff ?? []).map(s => ({ id: s.id, label: `Staff: ${s.displayName}` })),
	];

	const handleUserSelection = (id: string) => {
		setSelectedUserIds(prev =>
			prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
		);
		setPage(1);
	};

	const clearNameFilter = () => {
		setSelectedUserIds([]);
		setPage(1);
	};

	const handleAssignClick = (stepInstance: StepInstanceResponse, mode: 'assign' | 'reassign') => {
		setSelectedStepInstance(stepInstance);
		setAssignModalMode(mode);
		setIsAssignModalOpen(true);
	};

	const handleModalClose = () => {
		setIsAssignModalOpen(false);
		setSelectedStepInstance(null);
	};

	const handleAssignSuccess = () => {
		handleModalClose();
	};

	const handleUnassign = async (stepInstance: StepInstanceResponse) => {
		setUnassigningStepId(stepInstance.id);
		try {
			await unassignStep({ stepInstanceId: stepInstance.id }).unwrap();
			toast.success("Step unassigned successfully");
		} catch (error: any) {
			toast.error(error?.data?.message || "Failed to unassign step. Please try again.");
		} finally {
			setUnassigningStepId(null);
		}
	};

	const getSlaStatus = (stepInstance: StepInstanceResponse) => {
		if (stepInstance.isOverdue) {
			return { className: styles.overdue, text: "Overdue" };
		}

		const dueDate = new Date(stepInstance.due);
		const now = new Date();
		const hoursUntilDue = (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60);

		if (hoursUntilDue <= 24) {
			return { className: styles.warning, text: "Due Soon" };
		}

		const daysUntilDue = Math.ceil(hoursUntilDue / 24);
		return { className: styles.normal, text: `${daysUntilDue} Day${daysUntilDue > 1 ? "s" : ""}` };
	};

	const getSlaDisplay = (stepInstance: StepInstanceResponse): string => {
		if (!stepInstance.stepDefinition) return "-";

		const { slaDuration, slaDurationType } = stepInstance.stepDefinition;

		// slaDurationType: 0 = Hours, 1 = Days
		if (slaDurationType === 0) { // Hours
			return `${slaDuration} ${slaDuration === 1 ? "Hour" : "Hours"}`;
		}

		// Days
		return `${slaDuration} ${slaDuration === 1 ? "Day" : "Days"}`;
	};

	const shouldShowStaffColumn =
		stepStatus === StepStatus.Assigned || stepStatus === StepStatus.Overdue;

	const shouldShowActionButton = stepStatus !== StepStatus.Closed;

	const columns: Array<ColumnType<StepInstanceResponse>> = [
		{
			accessor: "requestType",
			header: "Service Request",
			render: (record: StepInstanceResponse) => (
				<span className={styles.requestType}>
					{record.customerServiceRequestInstance?.serviceRequest?.name ?? "-"}
				</span>
			),
		},
		{
			accessor: "customerName",
			header: "Name of Customer",
			render: (record: StepInstanceResponse) => (
				<span className={styles.customerName}>
					{record.customer ? `${record.customer.firstName} ${record.customer.lastName}` : "-"}
				</span>
			),
		},
		{
			accessor: "stepName",
			header: "Step",
			render: (record: StepInstanceResponse) => (
				<span className={styles.stepName}>{record.stepDefinition?.name ?? "-"}</span>
			),
		},
		// Conditionally add Staff Member column
		...(shouldShowStaffColumn ? [{
			accessor: "assignedUser" as const,
			header: "Staff Member",
			render: (record: StepInstanceResponse) => (
				<span className={styles.assignedTo}>
					{record.assignedUser
						? `${record.assignedUser.firstName} ${record.assignedUser.lastName}`
						: "-"}
				</span>
			),
		}] : []),
		{
			accessor: "dateTime",
			header: "Created At",
			render: (record: StepInstanceResponse) => (
				<span className={styles.dateTime}>{formatTimestamp(record.createdAt)}</span>
			),
		},
		{
			accessor: "sla",
			header: "SLA",
			render: (record: StepInstanceResponse) => (
				<span className={styles.slaValue}>{getSlaDisplay(record)}</span>
			),
		},
		{
			accessor: "status",
			header: "Status",
			render: (record: StepInstanceResponse) => {
				const slaStatus = getSlaStatus(record);
				return <span className={`${styles.slaBadge} ${slaStatus.className}`}>{slaStatus.text}</span>;
			},
		},
		// Conditionally add action button
		...(shouldShowActionButton ? [{
			accessor: "id" as const,
			header: "",
			render: (record: StepInstanceResponse) => {
				const showBothButtons = stepStatus === StepStatus.Assigned || stepStatus === StepStatus.Overdue;
				const isThisStepUnassigning = unassigningStepId === record.id;

				if (showBothButtons) {
					return (
						<div style={{ display: 'flex', gap: '8px' }}>
							<Can permission={UtilityPermissions.AssignStep}>
								<Button
									variant="warning"
									text="Reassign"
									onClick={() => handleAssignClick(record, 'reassign')}
									disabled={isThisStepUnassigning}
									className={styles.actionButton}
								/>
							</Can>
							<Can permission={UtilityPermissions.UnassignStep}>
								<Button
									variant="danger"
									text={isThisStepUnassigning ? "Unassigning..." : "Unassign"}
									onClick={() => handleUnassign(record)}
									disabled={isThisStepUnassigning}
									className={styles.actionButton}
								/>
							</Can>
						</div>
					);
				}

				return (
					<Can permission={UtilityPermissions.AssignStep}>
						<Button
							variant="info"
							text="Assign"
							onClick={() => handleAssignClick(record, 'assign')}
							disabled={false}
							className={styles.actionButton}
						/>
					</Can>
				);
			},
		}] : []),
	];

	const headerActions = (
		<div className={styles.tableHeaderActions}>
			<div className={styles.searchContainer}>
				<SearchInput
					initialValue={searchTerm}
					onSearch={(value: string) => setSearchTerm(value)}
					placeholder="Search step name"
				/>
				{isFetching && <ClipLoader size={20} color="#DE841B" />}
			</div>
			<div className={styles.filtersRow}>
				<div className={styles.filterGroup}>
					<span className={styles.filterLabel}>Name</span>
					<span className={styles.filterDivider} />
					<SearchableMultiselectDropdown
						items={allUsers}
						checkedIds={selectedUserIds}
						placeholder="Search by name or email"
						onSearch={(value: string) => setNameSearchTerm(value)}
						onSelectionChange={handleUserSelection}
						debounceTime={300}
						isLoading={isLoadingUsers}
					/>
					{selectedUserIds.length > 0 && (
						<Button text="Clear" variant="text" onClick={clearNameFilter} />
					)}
				</div>
				<div className={styles.filterGroup}>
					<span className={styles.filterLabel}>Date</span>
					<span className={styles.filterDivider} />
					<Button
						text={hasActiveDateFilter ? dateFilterLabel : "Add Filter"}
						variant="outline"
						onClick={onOpenDateFilter}
						leftIcon={<FilterIcon2 />}
					/>
					{hasActiveDateFilter && (
						<Button text="Clear" variant="text" onClick={onClearDateFilter} />
					)}
				</div>
			</div>
		</div>
	);

	return (
		<div className={styles.historyContainer}>
			<DataTable<StepInstanceResponse>
				data={tableData}
				loading={isFetching}
				isUpdating={isFetching}
				columns={columns}
				headerActions={headerActions}
				emptyComponent={
					<EmptyState icon={<WorkflowsMenuActiveIcon />} title="No steps found" />
				}
			/>
			<TablePagination
				isLoading={isFetching}
				totalItems={count ?? 0}
				itemsPerPage={pageSize}
				currentPage={page}
				onPageChange={setPage}
			/>

			{isAssignModalOpen && selectedStepInstance && (
				<AssignStepModal
					isOpen={isAssignModalOpen}
					onClose={handleModalClose}
					onSuccess={handleAssignSuccess}
					stepInstance={selectedStepInstance}
					departmentId={departmentId}
					mode={assignModalMode}
				/>
			)}
		</div>
	);
};
